import React, { useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import useCalendarNavigation
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/hook/useCalendarNavigation';
import Utilities from '@util/Utilities';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';

const DashboardSelectCalendarMonthFilter = React.memo(() => {

	const dashboardNavigation = useCalendarNavigation();
	const tools = useMarketStateTools();

	const nextMonth = useCallback(() => {
		tools.nextCalendarMonth();
		dashboardNavigation.reset();
	}, [tools.calendarMonth]);

	const previousMonth = useCallback(() => {
		tools.previousCalendarMonth();
		dashboardNavigation.reset();
	}, [tools.calendarMonth]);

	const set = useCallback((date: Date | null) => {
		if (!!date) {
			tools.setCalendarMonth({
				year: date.getFullYear(),
				month: Utilities.getMonthByIndex(date.getMonth()),
			});
		}
	}, [tools.calendarMonth]);

	const current = tools.calendarMonth;

	return !!current ?
		<Popover as="div" className="relative flex rounded-md bg-white items-stretch mb-2 lg:mb-0">
			<button onClick={previousMonth}
			        type="button"
			        className="flex h-8 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50">
				<span className="sr-only">Previous month</span>
				<ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
			</button>
			<PopoverButton
				type="button"
				className="border-y border-gray-300 px-3.5 h-8 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative w-full md:w-auto min-w-[9rem]">
				{current.display}
			</PopoverButton>
			<button onClick={nextMonth}
			        type="button"
			        className="flex h-8 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50">
				<span className="sr-only">Next month</span>
				<ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
			</button>
			<PopoverPanel className="absolute z-10 overflow-hidden top-6 mt-3 w-[218px] h-[218px] ">
				<DatePicker
					onChange={date => set(date)}
					dateFormat="MM/yyyy"
					calendarContainer={CalendarContainer}
					inline
					showMonthYearPicker
					selected={new Date(current.index.year, Utilities.getMonthOrdinal(current.index.month), 1)}
					renderCustomHeader={props => <CalendarHeaderFragment props={props} />}
				/>
			</PopoverPanel>
		</Popover> : <div />;
});

const CalendarContainer: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => <div
	className="float-left text-xs rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">{children}</div>;

const CalendarHeaderFragment: React.FunctionComponent<{ props: ReactDatePickerCustomHeaderProps }> = ({ props }) => {
	return <div className="flex justify-between items-center">
		<div className="ml-3 cursor-pointer"><ChevronLeftIcon onClick={props.decreaseYear} className="h-5 w-5"
		                                                      aria-hidden="true" /></div>
		<div className="text-sm font-semibold font-sans">{props.date.getFullYear()}</div>
		<div className="mr-3 cursor-pointer"><ChevronRightIcon onClick={props.increaseYear} className="h-5 w-5"
		                                                       aria-hidden="true" /></div>
	</div>;
};

export default DashboardSelectCalendarMonthFilter;